/**
 * Version utilities for the Doctor Portal application
 * Handles version display and build information
 */

/**
 * Get the application version from environment variables or package.json
 * @returns {string} The current app version (e.g., "1.0.0")
 */
export const getAppVersion = (): string => {
  // Try environment variable first (set during build)
  if (process.env.REACT_APP_VERSION) {
    return process.env.REACT_APP_VERSION;
  }

  // Fallback to package.json version
  try {
    // Note: This requires the package.json to be accessible at build time
    // In production builds, this will use the environment variable
    const packageJson = require('../../package.json');
    return packageJson.version;
  } catch (error) {
    console.warn('Could not load version from package.json, using fallback');
    return '1.0.0'; // Fallback version
  }
};

/**
 * Get the build date from environment variables or current date
 * @returns {string} The build date in YYYY-MM-DD format
 */
export const getBuildDate = (): string => {
  // Try environment variable first (set during build)
  if (process.env.REACT_APP_BUILD_DATE) {
    return process.env.REACT_APP_BUILD_DATE;
  }

  // Fallback to current date
  return new Date().toISOString().split('T')[0];
};

/**
 * Get full version information including version and build date
 * @returns {object} Object containing version and buildDate
 */
export const getVersionInfo = () => {
  return {
    version: getAppVersion(),
    buildDate: getBuildDate(),
    fullVersion: `v${getAppVersion()} (${getBuildDate()})`
  };
};

/**
 * Check if this is a development build
 * @returns {boolean} True if running in development mode
 */
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Get environment-specific version suffix
 * @returns {string} Version suffix like "-dev", "-staging", or empty string for production
 */
export const getVersionSuffix = (): string => {
  if (process.env.REACT_APP_ENVIRONMENT === 'development') {
    return '-dev';
  }
  if (process.env.REACT_APP_ENVIRONMENT === 'staging') {
    return '-staging';
  }
  return ''; // Production
};